import { GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, Optional, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ItemDetail, ItemDetailStatus } from "app/model/entities/entity-model";

import { AgFns, GridOptionsExt, ISortModel } from "app/shared/ag-fns";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { InventoryManagerComponent } from "./inventory-manager.component";

@Component({
  selector: "app-inventory-lookup",
  templateUrl: "./inventory-lookup.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InventoryLookupComponent extends DomainBaseComponent {
  resultsGridOptions: GridOptionsExt;
  resultsRowData: ItemDetail[] = [];
  allStatuses: ItemDetailStatus[] = [];
  selectedStatuses: ItemDetailStatus[] = [];
  searchText = "";
  itemIdInput = "";

  selectedItem: ItemDetail | null = null;
  searchInFlight = false;
  hasSearched = false;

  private readonly searchInput$ = new Subject<void>();

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    @Optional() private inventoryManager: InventoryManagerComponent,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => this.updateFromContext());

    if (this.inventoryManager) {
      setTimeout(() => {
        this.inventoryManager.showEoqButtons = false;
        this.inventoryManager.showAddInventoryButtons = false;
        this.inventoryManager.showPrintBarcodesButton = false;
        this.inventoryManager.showScannerControls = false;
      });
    }

    this.searchInput$
      .pipe(debounceTime(250), takeUntil(this.onDestroy))
      .subscribe(() => this.runSearch());
  }

  async updateFromContext() {
    this.allStatuses = _.sortBy(this.dbQueryService.getAllCached(ItemDetailStatus), (s) => s.id);

    this.resultsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onResultsGridReady,
      onRowSelected: this.onResultRowSelected.bind(this),
      getRowId: (p) => p.data?.id,
    });

    this.isPageReady = true;

    const seededItemId = this.route.snapshot.params["itemDetailId"];
    if (seededItemId) {
      this.itemIdInput = seededItemId;
      await this.findById();
    }
  }

  private onResultsGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "Inventory ID", field: "id" },
      { headerName: "Manufacturer", field: "product.productType.manufacturer.name" },
      { headerName: "Style", field: "product.productType.style" },
      { headerName: "Features", field: "product.featureChoicesExtract" },
      { headerName: "Description", field: "product.productType.description" },
      { headerName: "Account", field: "account.accountName" },
      { headerName: "Status", field: "itemDetailStatus.name" },
      { headerName: "Bin", field: "itemBin.name" },
      { headerName: "Last Scanned", field: "lastScanTs" },
    ];
    const sortModel = [
      { colId: "product.productType.manufacturer.name", sort: "asc" },
      { colId: "product.productType.style", sort: "asc" },
    ] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  private onResultRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    this.selectedItem = event.node.data as ItemDetail;
  }

  // --- ID lookup ---

  async findById() {
    const id = (this.itemIdInput || "").trim();
    if (!id) return;
    this.searchInFlight = true;
    try {
      const itd = await this.dbQueryService.getItemDetailById(id);
      this.hasSearched = true;
      if (!itd) {
        this.selectedItem = null;
        this.resultsRowData = [];
        return;
      }
      this.resultsRowData = [itd];
      this.selectedItem = itd;
      setTimeout(() => {
        this.resultsGridOptions.apiX?.forEachNode((node) => {
          if (node.data === itd) node.setSelected(true);
        });
      }, 1);
    } finally {
      this.searchInFlight = false;
    }
  }

  // --- Free-text search ---

  onSearchInputChanged() {
    this.searchInput$.next();
  }

  onSelectedStatusesChange() {
    this.searchInput$.next();
  }

  private async runSearch() {
    const text = (this.searchText || "").trim();
    const statusIds = (this.selectedStatuses || []).map((s) => s.id);
    if (text.length < 2 && statusIds.length === 0) {
      this.resultsRowData = [];
      this.hasSearched = false;
      return;
    }
    this.searchInFlight = true;
    try {
      const q = this.dbQueryService.createItemDetailSearchQuery(text, statusIds).take(200);
      const rows = (await q.execute()) as ItemDetail[];
      this.hasSearched = true;
      this.resultsRowData = rows;
      if (this.selectedItem && !rows.some((r) => r === this.selectedItem)) {
        this.selectedItem = null;
      }
    } finally {
      this.searchInFlight = false;
    }
  }
}
